import { z } from 'zod';

/**
 * O fio do OpenCode, o terceiro motor (F16, ADR-0022).
 *
 * Mora no protocolo porque as três pontas o leem: a API empurra a chave, o
 * daemon a aplica e relata, e o painel desenha o retrato por máquina.
 */

/**
 * O valor de esforço que diz "não mande `variant` nenhum": quem decide é o
 * provedor do modelo.
 *
 * É uma escolha EXPLÍCITA, e é gravada como tal — a regra da casa é esforço
 * explícito em toda sessão, e `padrao` é a forma explícita de não forçar. Um
 * campo ausente, ao contrário, seria o subprocesso escolhendo sozinho.
 */
export const ESFORCO_SEM_VARIANTE = 'padrao';

/**
 * O esforço de uma sessão OPENCODE (Interface congelada, item 3 — revisto pela
 * medição da Fase 0).
 *
 * **Não é um enum fixo, e isso é medição, não preguiça.** O esforço do
 * OpenCode viaja como `variant` por prompt, e cada modelo tem os SEUS, lidos do
 * catálogo interno do OpenCode: `deepseek/deepseek-v4.1-flash` tem
 * `[low, high, max]` (sem `medium`), `z-ai/glm-5.3` tem `[low, high, max]`,
 * `deepseek/deepseek-v4-pro` tem `[high, xhigh]` e `moonshotai/kimi-k2.7-code`
 * não tem nenhum. Um enum da casa estaria errado para metade dos favoritos no
 * primeiro dia — e o `none` que o plano previa não existe em lugar nenhum.
 *
 * O que o schema garante é só a FORMA: `padrao`, ou um nome curto em
 * minúsculas. Se aquele esforço existe para aquele modelo é pergunta do
 * catálogo, e quem responde é a API (`a2a`) — o fio não conhece catálogo.
 */
export const EsforcoOpencodeSchema = z.union([
  z.literal(ESFORCO_SEM_VARIANTE),
  z.string().regex(/^[a-z]{2,12}$/),
]);
export type EsforcoOpencode = z.infer<typeof EsforcoOpencodeSchema>;

/* -------------------------------------------------------------------------- */
/* O provedor dos modelos (F2 da revisão de out/2026, ADR-0025)                */
/* -------------------------------------------------------------------------- */

/**
 * Quem atende os modelos do OpenCode. O NanoGPT é o padrão desde a F2 (D11 do
 * plano de revisão); o OpenRouter fica guardado no código, desligado.
 *
 * Decisão por provedor se escreve num `switch` que termina em
 * `provedorOpencodeNaoTratado` — a mesma forma da regra dos motores
 * (`agente.md`): um terceiro provedor que chegue tem de passar por cada ramo,
 * e o compilador aponta onde.
 */
export const PROVEDORES_OPENCODE = ['nanogpt', 'openrouter'] as const;
export const ProvedorOpencodeSchema = z.enum(PROVEDORES_OPENCODE);
export type ProvedorOpencode = z.infer<typeof ProvedorOpencodeSchema>;

/** O fim de todo `switch` sobre o provedor: em tempo de compilação, `never`. */
export function provedorOpencodeNaoTratado(provedor: never): never {
  throw new Error(`provedor do OpenCode nao tratado: ${String(provedor)}`);
}

/**
 * Um modelo que o daemon DECLARA na config do `opencode serve`.
 *
 * Medido em 2026-10-26 (`medicoes/F2-nanogpt-api.md` do plano de revisão): um
 * id que o catálogo interno do binário não conhece TRAVA a sessão em silêncio
 * — nenhum erro, turno sem fim —, e o mesmo id declarado em
 * `provider.<x>.models` funciona. Sem preço declarado o binário relata custo
 * 0, então o preço viaja junto (USD por milhão de tokens, a unidade do
 * OpenCode e do NanoGPT). `null` é "o catálogo não disse", nunca zero.
 *
 * O `id` é o da casa (`nano-gpt/<id do NanoGPT>`, ou o do OpenRouter sem
 * prefixo): quem corta o prefixo é o daemon, num lugar só.
 */
export const ModeloDeclaradoOpencodeSchema = z.strictObject({
  id: z.string().max(200).regex(/^[^\s/]+\/[^\s]+$/),
  nome: z.string().min(1).max(200),
  contexto: z.number().int().positive().nullable(),
  imagem: z.boolean(),
  custo: z
    .strictObject({
      entrada: z.number().nonnegative(),
      saida: z.number().nonnegative(),
      cacheLido: z.number().nonnegative().nullable(),
    })
    .nullable(),
});
export type ModeloDeclaradoOpencode = z.infer<typeof ModeloDeclaradoOpencodeSchema>;

/** Teto da lista declarada: os favoritos (30) mais padrão, mestre e visão, com folga. */
export const TETO_DE_MODELOS_DECLARADOS = 40;

/* -------------------------------------------------------------------------- */
/* Os nomes dos eventos                                                        */
/* -------------------------------------------------------------------------- */

/**
 * API → daemon: a chave do OpenRouter (D5). Com ack, e o ack NUNCA a carrega.
 *
 * Constante, e não string solta no emissor e no ouvinte: um nome digitado
 * diferente dos dois lados não dá erro nenhum no socket — o evento só nunca
 * chega, e a tela ficaria mostrando "aplicada em 0 de Y" sem motivo.
 */
export const EVENTO_OPENCODE_CREDENCIAL = 'opencode.credencial';

/**
 * Daemon → API: o retrato do OpenCode nas TRANSIÇÕES (chave aplicada, processo
 * caiu, camada gerenciada divergiu). O pulso leva o mesmo retrato a cada volta;
 * este evento existe para a tela não esperar o próximo pulso para saber que a
 * troca da chave terminou.
 */
export const EVENTO_AGENTE_OPENCODE = 'agente.opencode';

/**
 * O primeiro daemon que sabe o que é `opencode.credencial`.
 *
 * A API só empurra a chave para quem tem a capacidade (`opencode` ou
 * `mestreOpencode`) E esta versão ou maior. Daemon anterior ignoraria o evento
 * — e ignorar é o melhor caso: o pior seria um daemon velho que um dia passasse
 * a registrar evento desconhecido em log, com a chave dentro.
 */
export const VERSAO_AGENTE_OPENCODE = '0.7.0' as const;

/**
 * O primeiro daemon que entende `provedor` e `modelos` em `opencode.credencial`
 * (F2 da revisão de out/2026).
 *
 * O daemon anterior tem a carga em `strictObject` SEM esses campos: ele
 * RECUSARIA a forma nova inteira, chave junto. Por isso a API só manda a forma
 * nova daqui para cima; para o daemon antigo, a forma de antes — com a chave do
 * OpenRouter se ele for o provedor ativo, ou `chave: null` se for o NanoGPT, que
 * o daemon antigo não sabe falar (ele derruba o OpenCode e recusa sessão nova,
 * D9, em vez de mandar a conversa a um provedor sem chave).
 */
export const VERSAO_AGENTE_NANOGPT = '0.8.0' as const;

/**
 * O intervalo mínimo entre dois empurrões da chave para a MESMA máquina, na
 * reconciliação pelo pulso. Sem piso, um daemon que recusasse a chave (camada
 * gerenciada divergente, por exemplo) receberia a chave de novo a cada pulso —
 * e cada envio é o segredo atravessando o fio mais uma vez.
 */
export const INTERVALO_MINIMO_DE_REEMPURRAR_CHAVE_MS = 5 * 60_000;

/* -------------------------------------------------------------------------- */
/* A chave: API → daemon                                                       */
/* -------------------------------------------------------------------------- */

/**
 * A carga de `opencode.credencial`.
 *
 * `versao` é a da linha em `segredos_frota`, e é ela que a tela compara para
 * dizer "aplicada em X de Y": a API nunca pergunta ao daemon QUAL chave ele
 * tem — só qual versão.
 *
 * `chave: null` é a REMOÇÃO: o daemon derruba os processos do OpenCode e passa
 * a recusar sessão nova (D9). Não existe "carga sem chave" que signifique
 * outra coisa.
 *
 * `strictObject`: um campo a mais aqui não é extensão, é a API mandando algo
 * que nenhum dos dois lados combinou — junto de um segredo.
 */
export const OpencodeCredencialSchema = z.strictObject({
  versao: z.number().int().positive(),
  chave: z.string().min(20).max(300).nullable(),
  /**
   * De quem é a chave (F2). Ausente é a API anterior à F2, que só conhecia o
   * OpenRouter — e é como o daemon a lê. Sem `.default()`: o default apagaria a
   * diferença entre "a API não disse" e "a API disse OpenRouter".
   */
  provedor: ProvedorOpencodeSchema.optional(),
  /** Os modelos a declarar na config do `serve` (F2). Ausente = nada declarado, como antes. */
  modelos: z.array(ModeloDeclaradoOpencodeSchema).max(TETO_DE_MODELOS_DECLARADOS).optional(),
});
export type OpencodeCredencial = z.infer<typeof OpencodeCredencialSchema>;

/**
 * O que o daemon fez com a chave.
 *
 * - `aplicada`: os processos do OpenCode já rodam com ela;
 * - `aguardando_turnos`: há turno OpenCode em andamento, e o daemon reinicia
 *   o processo quando o último terminar — ou aos 10 min, abortando (D9);
 * - `sem_opencode`: esta máquina não tem o OpenCode ligado. A chave foi
 *   DESCARTADA, e não guardada "para depois": segredo sem uso não fica em
 *   memória de processo nenhum.
 */
export const EstadoDaCredencialNoAckSchema = z.enum(['aplicada', 'aguardando_turnos', 'sem_opencode']);
export type EstadoDaCredencialNoAck = z.infer<typeof EstadoDaCredencialNoAckSchema>;

/**
 * O ack de `opencode.credencial`. **Nunca carrega a chave**, nem pedaço dela.
 *
 * `strictObject` nos dois ramos é a trava: um daemon que ecoasse o pedido
 * inteiro no ack (o jeito mais natural de "confirmar o que recebeu") seria
 * RECUSADO no parse, em vez de ter o segredo gravado em log ou auditoria pela
 * API. Com `z.object`, a chave extra seria descartada calada — e o eco seguiria
 * vivo no daemon sem ninguém saber.
 */
export const OpencodeCredencialAckSchema = z.discriminatedUnion('ok', [
  z.strictObject({
    ok: z.literal(true),
    versao: z.number().int().positive(),
    estado: EstadoDaCredencialNoAckSchema,
  }),
  z.strictObject({
    ok: z.literal(false),
    erro: z.string().min(1).max(80),
    mensagem: z.string().max(300),
  }),
]);
export type OpencodeCredencialAck = z.infer<typeof OpencodeCredencialAckSchema>;

/* -------------------------------------------------------------------------- */
/* O retrato: daemon → API                                                     */
/* -------------------------------------------------------------------------- */

/**
 * O formato das chaves dos provedores do OpenCode, para REDIGIR — nunca para
 * validar: `sk-or-…` (OpenRouter) e `sk-nano-<uuid>` (NanoGPT, F2).
 *
 * Global e sem âncora: o que se procura é a chave no meio de uma frase de erro
 * ("401 para sk-or-v1-…"), que é exatamente onde ela escaparia.
 */
const CHAVE_DO_OPENCODE_NO_TEXTO = /sk-(?:or|nano)-[A-Za-z0-9_-]+/g;

/** O que fica no lugar da chave. Curto, e diz o que houve. */
export const CHAVE_REDIGIDA = '[chave redigida]';

/**
 * Tira do texto qualquer coisa com cara de chave de provedor do OpenCode.
 *
 * Exportada porque o daemon faz o mesmo antes de escrever log, e duas regras
 * de "o que parece chave" divergiriam no dia em que o formato mudar. Chamava
 * `redigirChaveOpencode` até a F2 — o nome mentiria com o NanoGPT ativo.
 */
export function redigirChaveOpencode(texto: string): string {
  return texto.replace(CHAVE_DO_OPENCODE_NO_TEXTO, CHAVE_REDIGIDA);
}

/** Um `opencode serve` de um perfil (`trabalho` no PC, `mestre` na VPS). */
export const ProcessoOpencodeSchema = z.object({
  dePe: z.boolean(),
  /** `null` é "não medido" — número inventado seria o painel afirmando o que não apurou. */
  rssMb: z.number().nonnegative().nullable(),
  sessoes: z.number().int().nonnegative(),
});
export type ProcessoOpencode = z.infer<typeof ProcessoOpencodeSchema>;

/**
 * Onde a chave está NESTA máquina. `ausente` é "nunca recebeu, ou recebeu
 * `null`"; `reiniciando` é o intervalo curto entre derrubar e subir de novo o
 * processo com a chave nova; `erro` vem com o motivo em `erro`, logo abaixo.
 */
export const EstadoDaChaveOpencodeSchema = z.enum([
  'ausente',
  'aplicada',
  'aguardando_turnos',
  'reiniciando',
  'erro',
]);
export type EstadoDaChaveOpencode = z.infer<typeof EstadoDaChaveOpencodeSchema>;

/**
 * A camada gerenciada (`C:\ProgramData\opencode`, `/etc/opencode`) conferida
 * pela sonda: dono e ACL, e o hash do arquivo contra o que o instalador gravou.
 * É a ÚNICA barreira contra um `opencode.json` de repositório afrouxando a
 * permissão (OC-11/OC-12); fora de `ok`, o host recusa sessão nova.
 */
export const CamadaGerenciadaSchema = z.enum(['ok', 'ausente', 'acl_frouxa', 'divergente']);
export type CamadaGerenciada = z.infer<typeof CamadaGerenciadaSchema>;

/**
 * O OpenCode visto da máquina que o hospeda, no pulso e em `agente.opencode`.
 *
 * **Três estados, não dois**, pela mesma razão do `AgenteCodexSchema`: ausente
 * (`null` no pulso) é "esta máquina não tem OpenCode configurado"; `ok: false`
 * é "tem, e não está bem". Colapsar os dois faria toda máquina sem OpenCode
 * parecer OpenCode quebrado.
 *
 * **Nada sensível aqui**, e a razão é concreta: `GET /maquinas` devolve a linha
 * inteira, e este retrato mora em `maquinas.opencode`. Da chave viaja só a
 * VERSÃO; e o único texto livre, `erro`, passa por `redigirChaveOpencode`
 * NO PARSE — o daemon já redige antes de mandar, e esta é a segunda barreira,
 * independente da primeira.
 *
 * `z.object` (que descarta chave desconhecida) e não `strictObject`: o retrato
 * cresce com o tempo, e um campo novo de um daemon mais novo não pode derrubar
 * o pulso inteiro numa API mais velha. Descartar é o seguro aqui — o que não
 * foi combinado não chega à coluna.
 */
export const AgenteOpencodeSchema = z.object({
  ok: z.boolean(),
  medidoEm: z.iso.datetime(),
  /** A versão do binário, lida com o ambiente isolado (OC-2). `null` = não apurada. */
  versao: z.string().max(40).nullable(),
  processos: z.object({
    trabalho: ProcessoOpencodeSchema.optional(),
    mestre: ProcessoOpencodeSchema.optional(),
  }),
  chave: z.object({
    versao: z.number().int().positive().nullable(),
    estado: EstadoDaChaveOpencodeSchema,
    desde: z.iso.datetime().nullable(),
  }),
  camadaGerenciada: CamadaGerenciadaSchema,
  /** Por que não está bem. Texto para humano, redigido no parse. */
  erro: z.string().max(200).transform(redigirChaveOpencode).nullable(),
});
export type AgenteOpencode = z.infer<typeof AgenteOpencodeSchema>;
